/* eslint-disable replo/no-tailwind-literal-color */
import type { ChatMessage } from "schemas/generated/chatMessage";

import React, { useEffect, useState } from "react";

import useCurrentUser from "@editor/hooks/useCurrentUser";
import { getTokenFromStorage } from "@editor/infra/auth";
import { selectProjectId } from "@editor/reducers/core-reducer";
import { useEditorSelector } from "@editor/store";
import { trpc } from "@editor/utils/trpc";

import { getPublisherUrl } from "@/config";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import Button from "@replo/design-system/components/button/Button";
import { Textarea } from "@replo/design-system/components/textarea/Textarea";
import { skipToken } from "@tanstack/react-query";
import { v4 } from "uuid";

import { useReactRenderStore } from "../store/reactRenderStore";
import { getSandpackFilePath } from "../utils/reactFilesUtils";

function EditorChat() {
  const projectId = useEditorSelector(selectProjectId);
  const { user } = useCurrentUser();
  const {
    prompt,
    setPrompt,
    loading,
    setLoading,
    openPages,
    chatId: chatIdStore,
    displayMode,
    setDisplayMode,
  } = useReactRenderStore();

  const { data, refetch: refetchReactFiles } = trpc.reactFile.list.useQuery(
    projectId ? { projectId } : skipToken,
  );
  const { data: chats } = trpc.chat.listAllOrCreate.useQuery(
    projectId ? { projectId } : skipToken,
  );

  const reactFiles = data?.reactFiles ?? [];
  const chatId = chatIdStore ?? chats?.[0]?.id;

  const { data: chat } = trpc.chat.get.useQuery(
    projectId && chatId ? { chatId, projectId } : skipToken,
  );

  const [messages, setMessages] = useState<ChatMessage[]>(chat?.messages ?? []);

  useEffect(() => {
    setMessages(chat?.messages ?? []);
  }, [chat]);

  const { mutate: initializeFiles } =
    trpc.reactFile.initializeFiles.useMutation({
      onSuccess: () => {
        void refetchReactFiles();
      },
    });

  const { mutate: loadFullVersionHistory } =
    trpc.reactFile.loadFullVersionHistory.useMutation({
      onSuccess: () => {
        void refetchReactFiles();
      },
    });

  const handleSubmit = async (e: React.FormEvent) => {
    try {
      e.preventDefault();
      setLoading(true);
      const PUBLISHER_URL = getPublisherUrl();
      const token = getTokenFromStorage();

      if (!chatId) {
        return;
      }

      setMessages((prev) => [
        ...prev,
        {
          id: v4(),
          content: prompt,
          createdAt: new Date(),
          role: "user",
          chatId,
        },
        {
          id: v4(),
          content: "",
          createdAt: new Date(),
          role: "assistant",
          chatId,
        },
      ]);

      setPrompt("");

      await fetchEventSource(
        `${PUBLISHER_URL}/api/v2/ai/build-assistant-react`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
          body: JSON.stringify({
            userPrompt: prompt,
            projectId,
            files: reactFiles.map((file) => ({
              path: getSandpackFilePath(file),
              content: file.content,
            })),
            openPages,
            chatId,
          }),
          onmessage(ev) {
            setMessages((prev) => {
              const lastIndex = prev.length - 1;
              if (lastIndex < 0) {
                return prev;
              }

              if (prev[lastIndex]?.role === "assistant") {
                const updated = [...prev];
                updated[lastIndex] = {
                  ...updated[lastIndex],
                  id: updated[lastIndex]?.id ?? v4(),
                  createdAt: new Date(),
                  role: "assistant",
                  content: `${updated[lastIndex]?.content}${ev.data}`,
                  chatId: updated[lastIndex]?.chatId ?? chatId,
                };
                return updated;
              }
              return prev;
            });
          },
          onclose() {
            setLoading(false);
            void refetchReactFiles();
          },
          onerror(err) {
            setLoading(false);
            console.error("Streaming error:", err);
          },
        },
      );

      void refetchReactFiles();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 w-80 flex-col rounded-lg border border-gray-200 bg-white p-2 shadow">
      <div className="flex h-full flex-col gap-3 overflow-y-auto pb-2">
        {messages.length > 0 ? (
          messages.map((message) =>
            message.role === "user" ? (
              <div
                key={message.id}
                className="max-w-[80%] self-end rounded-lg bg-gray-100 px-4 py-2 text-base break-words text-gray-900"
              >
                {message.content}
              </div>
            ) : (
              <AssistantMessage key={message.id} message={message} />
            ),
          )
        ) : (
          <div className="flex h-full items-center justify-center">
            <div className="text-base text-gray-500">
              Enter a prompt to get started!
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2 pt-2">
        <Textarea value={prompt} onChange={(e) => setPrompt(e)} />
        <div className="flex gap-2">
          <Button
            onClick={(e) => void handleSubmit(e)}
            isLoading={loading}
            variant="primary"
            type="submit"
          >
            Send
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              initializeFiles({
                projectId: projectId ?? "",
                userId: user?.id ?? "",
              })
            }
          >
            Initialize Files
          </Button>
          {displayMode === "preview" && (
            <Button variant="secondary" onClick={() => setDisplayMode("edit")}>
              Edit
            </Button>
          )}
          {displayMode === "edit" && (
            <Button
              variant="secondary"
              onClick={() => setDisplayMode("preview")}
            >
              Preview
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() =>
              loadFullVersionHistory({
                projectId: projectId ?? "",
              })
            }
          >
            History
          </Button>
        </div>
      </div>
    </div>
  );
}
const parseAssistantMessage = (content: string) => {
  const tags = ["<replo-write", "<replo-change-summary"];
  let result = content;

  for (const tag of tags) {
    const startIdx = result.indexOf(tag);
    const endTag = tag.replace("<", "</");
    const endIdx = result.indexOf(`endTag  ">"`);

    if (startIdx !== -1 && endIdx === -1) {
      return `${result.slice(0, startIdx).replace(/\s*$/, "")}Thinking...`;
    }

    if (startIdx !== -1 && endIdx !== -1) {
      result = result.replace(
        new RegExp(`${tag}[\\S\\s]*?${endTag}>`, "g"),
        "",
      );
    }
  }

  return result;
};

const AssistantMessage = ({ message }: { message: ChatMessage }) => {
  const parsedContent = parseAssistantMessage(message.content);

  if (parsedContent === "") {
    return null;
  }

  return (
    <div className="flex max-w-full flex-col gap-2 px-4 py-2 text-base break-words text-gray-900">
      <div className="flex items-center gap-3 text-sm font-medium text-gray-500">
        <div>Replo AI</div>
        <div className="text-xs italic">
          {new Date(message.createdAt).toLocaleTimeString()}
        </div>
      </div>
      <div>{parsedContent}</div>
    </div>
  );
};

export default EditorChat;
